// src/oura/ask/live.ts
//
// Ask Oura's answers that need a server call (3 Nov 2026), on top of the word
// matching in answers.ts, which stays the first answer for every question:
//   - an action ("tick…", "mark… fixed", "draft the client note"): the server
//     previews it and the chat shows a card with Do it (actions.ts);
//   - "what happened since…": the client's change log (ourteam-backend #6797);
//   - anything the word matching cannot answer: Oura's AI step reads the
//     client's brief and answers in 1 to 4 sentences (#6795), with the screens
//     it read as citations. When the AI does not answer, the "Did you mean"
//     the matching already built is shown instead.
// Each piece is behind its own switch (ask.ts), so with all three off this
// file returns null for everything and the chat is exactly as before.

import { ASK_ACTIONS_ON, ASK_AI_ON, ASK_CHANGES_ON, ASK_IMAGES_ON, askOuraAi, getBusinessChanges, type AskAiTurn, type AskCite } from "@service/aeo/ask";
import { OURA_ROOT } from "../nav";
import { dayMonth } from "../visibility/format";
import { actionIntent, actionPreviewAnswer, looksLikeAction } from "./actions";
import { answerText } from "./answers";
import { hrefs, n, p, s, src } from "./text";
import type { AskAnswer, AskImage, AskMessage, AskRow, AskScope, AskSource } from "./types";
import type { ClientData } from "./useAskData";

/** A question answered by a server call: what the chat shows while it waits, and the answer. */
export interface LiveAsk {
    /** "Asking Oura's AI about The Lush Clinic". */
    waiting: string;
    answer: Promise<AskAnswer>;
}

/** The last turns of the thread, for the AI step (the server keeps 6). */
export function historyOf(messages: AskMessage[]): AskAiTurn[] {
    const turns: AskAiTurn[] = [];
    for (let i = 0; i < messages.length; i += 1) {
        const m = messages[i];
        if (m.from === "staff") {
            // The question being asked now is sent as `question`, not again as a turn.
            const reply = messages[i + 1];
            if (reply?.from === "oura" && reply.answer === null) continue;
            turns.push({ role: "user", text: m.text });
        } else if (m.answer) turns.push({ role: "oura", text: answerText(m.answer).slice(0, 600) });
    }
    return turns.slice(-6);
}

/* ------------------------------------------------------------------------- *
 * The change log
 * ------------------------------------------------------------------------- */

const SINCE = /\b(what(?:'s| is| has)? (?:happened|new|changed|been (?:done|going on))|happened|anything new|recent(?:ly)?\b.*\b(?:change|happen)|change log|history of changes|what did (?:we|oura) do)\b/;
const WINDOW = /\b(?:since|in|over|during|for) (?:the )?(?:last|past|previous)?\s*(?:(\d+) (day|week|month)s?|(week|month|fortnight)|i (?:last )?(?:looked|checked))\b|\b(last|past|this) (week|month|fortnight)\b/;

/** "What happened since…": the start day (YYYY-MM-DD, local) and its label; null for the server's 30-day default. */
export function changesIntent(t: string): { since: string | null; label: string } | null {
    // "What changed since the last scan" is the matching's own changed intent.
    if (!SINCE.test(t) || /\b(scan|check)\b/.test(t)) return null;
    const m = WINDOW.exec(t);
    let days = 30;
    let label = "the last 30 days";
    if (m) {
        const count = m[1] ? parseInt(m[1], 10) : 1;
        const unit = (m[2] ?? m[3] ?? m[5] ?? "month").toLowerCase();
        days = unit === "day" ? count : unit === "week" ? count * 7 : unit === "fortnight" ? 14 : count * 30;
        label = m[3] === "fortnight" || m[5] === "fortnight" ? "the last two weeks" : `the last ${count === 1 ? unit : `${count} ${unit}s`}`;
        if (m[4] === "this") label = `this ${m[5]}`;
    }
    if (days === 30 && !m) return { since: null, label };
    // The server takes a Malaysian date, so the window is counted on Malaysia's calendar wherever the staff member sits.
    const d = malaysiaToday();
    if (m?.[4] === "this" && m[5] === "week") d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); // since Monday
    else if (m?.[4] === "this" && m[5] === "month") d.setUTCDate(1);
    else d.setUTCDate(d.getUTCDate() - days);
    return { since: d.toISOString().slice(0, 10), label };
}

/** Today's date in Malaysia, as a UTC-midnight Date so day arithmetic stays on the calendar. */
function malaysiaToday(): Date {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuala_Lumpur", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts();
    const at = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((x) => x.type === type)?.value);
    return new Date(Date.UTC(at("year"), at("month") - 1, at("day")));
}

const EVENT_WORD: Record<string, string> = {
    scan_finished: "Scan",
    page_live: "Live",
    answers_approved: "Approved",
    step_done: "Done",
    step_came_back: "Came back",
    step_late: "Late",
    request_opened: "Request",
    request_closed: "Request",
    doc_shared: "Shared",
    contract_activated: "Contract",
    contract_renewed: "Contract",
    access_connected: "Access",
    payment_received: "Payment",
};

async function changesAnswer(d: ClientData, since: string | null, label: string): Promise<AskAnswer> {
    const rows = await getBusinessChanges(d.businessId, since ?? undefined);
    const history = src("History", hrefs.history(d.businessId));
    if (rows === null) {
        return { blocks: [p(s(`Oura could not read ${d.name}'s change log just now. Ask again in a moment.`, history))], followUps: ["Catch me up"] };
    }
    if (rows.length === 0) {
        return { blocks: [p(s(`Nothing is on ${d.name}'s change log for ${label}: no scan, page change, approval, step, request, share or payment.`, history))], followUps: ["What needs me today?", "Catch me up"] };
    }
    const shown = rows.slice(0, 12);
    const late = rows.filter((r) => r.event === "step_late" || r.event === "step_came_back").length;
    const list: AskRow[] = shown.map((r) => ({
        title: r.text,
        meta: `${EVENT_WORD[r.event] ?? r.event.replace(/_/g, " ")} · ${dayMonth(r.at).replace(/ \d{4}$/, "")}`,
        late: r.event === "step_late" || r.event === "step_came_back",
        to: r.link,
    }));
    return {
        blocks: [
            p(s(`${n(rows.length, "thing")} happened on ${d.name} in ${label}${late ? `, ${n(late, "step")} late or come back` : ""}.`, history)),
            { kind: "list", title: `Since ${dayMonth(rows[rows.length - 1].at)}`, tone: late ? "amber" : "gray", rows: list, more: rows.length > shown.length ? `${rows.length - shown.length} more on the History tab` : undefined },
        ],
        followUps: ["What needs me today?", "What's waiting on us?"],
    };
}

/* ------------------------------------------------------------------------- *
 * The AI step
 * ------------------------------------------------------------------------- */

function citeSource(c: AskCite, scope: AskScope): AskSource | null {
    if (scope.kind === "all") {
        if (c === "delivery" || c === "requests" || c === "overview") return src("Businesses", hrefs.today());
        if (c === "documents") return src("Documents", hrefs.allDocs());
        return src("Businesses", hrefs.businesses());
    }
    const id = scope.businessId;
    const base = `${OURA_ROOT}/businesses/${encodeURIComponent(id)}`;
    switch (c) {
        case "overview":
            return src("Overview", hrefs.overview(id));
        case "delivery":
            return src("Delivery", hrefs.delivery(id));
        case "visibility":
            return src("Visibility", hrefs.visibility(id));
        case "history":
            return src("History", hrefs.history(id));
        case "client":
        case "requests":
            return src("Client", hrefs.client(id));
        case "documents":
            return src("Documents", `${base}/documents`);
        case "site":
            return src("Site", hrefs.site(id));
        case "search":
            return src("Overview", hrefs.overview(id));
        case "setup":
            return src("Onboarding", `${base}/onboarding`);
        default:
            return null;
    }
}

async function aiAnswer(question: string, scope: AskScope, name: string, history: AskAiTurn[], fallback: AskAnswer, images?: AskImage[]): Promise<AskAnswer> {
    const pics = images && images.length ? images.map((i) => ({ name: i.name, mime: i.mime, data: i.data })) : undefined;
    const got = await askOuraAi(scope.kind === "client" ? scope.businessId : null, question, history, pics);
    if (!got.ok) {
        const lead = got.code === "refused" ? got.message : pics ? "Oura's AI could not read the picture in time." : "Oura's AI did not answer in time.";
        const first = fallback.blocks[0];
        const rest = fallback.blocks.slice(1);
        const blocks = first?.kind === "p" ? [p(s(`${lead} ${first.sentences.map((x) => x.text).join(" ")}`)), ...rest] : [p(s(lead)), ...fallback.blocks];
        return { blocks, followUps: fallback.followUps };
    }
    const sources: AskSource[] = [];
    for (const c of got.reply.cites) {
        const x = citeSource(c, scope);
        if (x && !sources.some((y) => y.to === x.to)) sources.push(x);
    }
    const text = got.reply.answer.trim().replace(/\s+/g, " ");
    return {
        blocks: [p(s(text, ...sources), s(`From Oura's AI, reading ${pics ? `your ${pics.length === 1 ? "picture" : "pictures"} and ` : ""}${scope.kind === "client" ? `${name}'s` : "every client's"} brief.`))],
        followUps: got.reply.follow_ups.slice(0, 3),
    };
}

/* ------------------------------------------------------------------------- *
 * The entry
 * ------------------------------------------------------------------------- */

/**
 * The server-backed answer for a question, or null when the matching's own
 * answer (`sync`) stands. `sync` is null only while the records are not read,
 * and then nothing here runs either: the facts come first.
 */
export function liveAnswer(text: string, scope: AskScope, d: ClientData | null, name: string, history: AskAiTurn[], sync: AskAnswer | null, images?: AskImage[]): LiveAsk | null {
    if (!sync) return null;
    // A picture always goes to the AI step: the word matching cannot see it.
    if (ASK_IMAGES_ON && ASK_AI_ON && images && images.length) {
        return { waiting: `Reading your ${images.length === 1 ? "picture" : "pictures"}`, answer: aiAnswer(text, scope, name, history, sync, images) };
    }
    const t = text.toLowerCase().replace(/[’]/g, "'").trim();
    if (scope.kind === "client" && d) {
        if (ASK_ACTIONS_ON && looksLikeAction(t)) {
            const m = actionIntent(t, d);
            if (m?.kind === "action") return { waiting: `Checking ${m.ask.label} with Oura`, answer: actionPreviewAnswer(m.ask, d) };
            if (m?.kind === "answer") return { waiting: `Reading ${name}'s Delivery`, answer: Promise.resolve(m.answer) };
        }
        if (ASK_CHANGES_ON) {
            const c = changesIntent(t);
            if (c) return { waiting: `Reading ${name}'s change log`, answer: changesAnswer(d, c.since, c.label) };
        }
    }
    if (ASK_AI_ON && sync.unsure) {
        return { waiting: `Asking Oura's AI about ${scope.kind === "client" ? name : "your clients"}`, answer: aiAnswer(text, scope, name, history, sync) };
    }
    return null;
}
